/**
 * MONSTER TOWN — THE BACKDROP: SKY, STARS AND THE MOON.
 * TIME-OF-DAY CREW (src/time/**).
 *
 * WHY THE STARS ARE PAINTED INTO THE SKY AND NOT MODELLED IN 3D
 * The obvious build is a star field in the world, drawn behind everything. On
 * this renderer that needs a material that blends but is NOT in the transparent
 * queue (or it draws over the town), a per-frame transform sync against a camera
 * another crew owns, and two or three extra draw calls on the device the README
 * says has ~2 ms of headroom. The sky is ALREADY a canvas stretched across the
 * glass, and the ground and the buildings already cover the part of it that is
 * not sky. So the stars go in the canvas: they are occluded correctly by
 * construction, they cost ZERO draw calls, and "the stars wheel" during the bed
 * fade is one rotation angle.
 *
 * The honest trade: they are locked to the screen, so they do not parallax when
 * the camera turns. Under a fixed iso camera that snaps in 90-degree steps that
 * is the better-looking half of the trade anyway — a wheeling star field on
 * every rotate-button press would be motion nobody asked for.
 *
 * WHY IT IS ONE REPAINTED CANVAS AND NOT N BAKED TEXTURES
 * `scene.background` takes exactly one texture, so a crossfade between a "day
 * sky" and a "night sky" is not something the renderer can do; a ladder of baked
 * skies pops, and costs VRAM on the device that has the least of it. There is
 * ONE canvas, repainted only when the picture would visibly change: a 2/255
 * delta on a gradient stop, 0.02 on the night level, a moved wheel or a twinkle
 * tick. Across a sixteen-minute day that is a few dozen uploads, nearly all of
 * them inside the two-minute dusk.
 *
 * AND WHY A REPAINT IS CHEAP. Ninety-six radial gradients is NOT something to do
 * at 24 Hz on a four-times-throttled CPU, so the stars are rendered ONCE into
 * two offscreen sheets (two, so they can twinkle out of phase) and a repaint is
 * a gradient fill, a moon, and two rotated blits.
 *
 * WHOSE OBJECT IT IS. The interiors crew swaps `scene.background` on the way
 * into a room and puts back whatever it found (`src/interior/ambience.ts`), so
 * we install ours ONCE and only ever mutate the canvas behind it. Both crews
 * stay correct and neither imports the other.
 */

import * as THREE from 'three'
import { cssHex, type DayLook } from './look'
import { hex } from '../core/palette'

/**
 * 256, not the art kit's 128. The shipped sky is a bare gradient and survives
 * being stretched from 128; a star has to still be a star at 1180 px wide.
 */
const SIZE = 256
/** Below this nobody can see the difference, so nobody pays for the upload. */
const REPAINT_DELTA = 2
const NIGHT_DELTA = 0.02

const STAR_COUNT = 150
/**
 * THE DREAM FIELD IS DENSER, AND IT IS THE SAME FIELD.
 *
 * Critic round 2: *"the stars are sparse white specks that read as dust or noise
 * rather than a sky."* Counted rather than argued. The bed fade's layer is
 * `inset: -48%` — about twice the glass in each direction, so a rotation never
 * swings an empty corner in — and `background-size: 100% 100%` stretches the
 * square sheet across it. Roughly a QUARTER of the field is therefore on screen
 * at once: ~37 stars over a whole iPad, which is a dusting.
 *
 * The dream sheet draws MORE of them and draws each one bigger. It is the same
 * seeded RNG consumed in the same order, so stars 0..149 land in exactly the
 * same places as the backdrop's — the two still cannot disagree; there are
 * simply more of them once the backdrop's run out.
 */
const DREAM_STAR_COUNT = 620
/** Stars fade out toward the horizon rather than stopping at a line. */
const STAR_FADE_FROM = 0.5
const STAR_FADE_TO = 0.8
/** The celestial pole the sky turns around, in canvas fractions. */
const POLE_X = 0.5
const POLE_Y = 1.02

const STAR_COLOR = cssHex(hex('flowerWhite'))
const MOON_COLOR = cssHex(hex('sunWarm'))
const MOON_SHADE = cssHex(hex('flowerWhite'))

let canvas: HTMLCanvasElement | null = null
let ctx2d: CanvasRenderingContext2D | null = null
let texture: THREE.CanvasTexture | null = null
/** Two sheets so half the stars can breathe out of phase with the other half. */
let sheets: HTMLCanvasElement[] = []
let moon = { r: 0, a: 0 }
let moonPoleY = POLE_Y
let repaints = 0

interface PaintedState {
  top: number
  mid: number
  horizon: number
  night: number
  wheel: number
  twinkle: number
}
let painted: PaintedState | null = null

// ---------------------------------------------------------------------------
// the star field (deterministic — the same sky every session and in every
// screenshot, so one capture can be compared with the last)
// ---------------------------------------------------------------------------

function makeRng(seed: number): () => number {
  let s = seed >>> 0 || 1
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0
    return s / 0x100000000
  }
}

function withAlpha(css: string, a: number): string {
  const v = parseInt(css.slice(1), 16)
  const clamped = a < 0 ? 0 : a > 1 ? 1 : a
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${clamped.toFixed(3)})`
}

/**
 * THE FIELD, at whatever resolution the caller needs.
 *
 * `half` splits it in two so the backdrop can breathe one half out of phase with
 * the other; `radiusMul` exists because the same field is drawn at two very
 * different sizes and a star has to come out the same size ON THE GLASS in both.
 * The 256px backdrop is stretched ~4.6x across an iPad, the 1024px overlay sheet
 * only ~2.3x — draw both with the same radii and the overlay's stars come out as
 * enormous faint smudges. (They did. The first bed-fade capture had a full
 * star field at opacity 1 and not one star you could see.)
 */
function paintField(
  c: CanvasRenderingContext2D,
  S: number,
  radiusMul: number,
  half: 0 | 1 | 2,
  horizonFade = true,
  count = STAR_COUNT,
  /**
   * How far the soft halo reaches past the star itself. The backdrop wants a
   * generous bloom (its stars are 1-2 px and would otherwise not survive being
   * stretched 4.6x); the DREAM sheet wants a tight one, because a field of
   * mid-sized soft dots does not read as a sky, it reads as FALLING SNOW.
   */
  glowSpread = 3.4,
): void {
  const rnd = makeRng(0x5741)
  const px = (horizonFade ? POLE_X : OVERLAY_POLE.x) * S
  const py = (horizonFade ? POLE_Y : OVERLAY_POLE.y) * S
  for (let i = 0; i < count; i++) {
    const x = rnd() * S
    const y = rnd() * S * (horizonFade ? 0.84 : 1)
    const size = (0.25 + rnd() * rnd() * 0.95) * (S / 256) * radiusMul
    const bright = 0.5 + rnd() * 0.5
    if (half !== 0 && i % 2 !== half - 1) continue
    const f = y / S
    const fade = !horizonFade
      ? 1
      : f <= STAR_FADE_FROM
        ? 1
        : f >= STAR_FADE_TO
          ? 0
          : 1 - (f - STAR_FADE_FROM) / (STAR_FADE_TO - STAR_FADE_FROM)
    const alpha = bright * fade
    if (alpha <= 0.02) continue
    const r = size * glowSpread
    const g = c.createRadialGradient(x, y, 0, x, y, r)
    g.addColorStop(0, withAlpha(STAR_COLOR, alpha))
    g.addColorStop(0.32, withAlpha(STAR_COLOR, alpha * 0.45))
    g.addColorStop(1, withAlpha(STAR_COLOR, 0))
    c.fillStyle = g
    c.beginPath()
    c.arc(x, y, r, 0, Math.PI * 2)
    c.fill()
  }
  // The pole is a fraction, so the moon's polar coordinates are resolution-free.
  const mx = 0.76 * S
  const my = 0.17 * S
  moon = { r: Math.hypot(mx - px, my - py) / S, a: Math.atan2(my - py, mx - px) }
  moonPoleY = horizonFade ? POLE_Y : OVERLAY_POLE.y
}

function buildSheets(): void {
  sheets = []
  moonPoleY = POLE_Y
  for (let i = 0; i < 2; i++) {
    const c = document.createElement('canvas')
    c.width = SIZE
    c.height = SIZE
    const cc = c.getContext('2d')
    if (cc === null) throw new Error('time: no 2d context for the stars')
    paintField(cc, SIZE, 1, i === 0 ? 1 : 2)
    sheets.push(c)
  }
}

function channelDelta(a: number, b: number): number {
  return Math.max(
    Math.abs(((a >> 16) & 255) - ((b >> 16) & 255)),
    Math.abs(((a >> 8) & 255) - ((b >> 8) & 255)),
    Math.abs((a & 255) - (b & 255)),
  )
}

// ---------------------------------------------------------------------------
// painting
// ---------------------------------------------------------------------------

function paintMoon(c: CanvasRenderingContext2D, night: number, wheel: number, S = SIZE): void {
  const px = POLE_X * S
  const py = moonPoleY * S
  const a = moon.a + wheel
  const x = px + Math.cos(a) * moon.r * S
  const y = py + Math.sin(a) * moon.r * S
  const r = S * 0.05
  if (y > S * 0.84 || y < -r * 4) return
  const halo = c.createRadialGradient(x, y, r * 0.7, x, y, r * 3.6)
  halo.addColorStop(0, withAlpha(MOON_COLOR, 0.3 * night))
  halo.addColorStop(1, withAlpha(MOON_COLOR, 0))
  c.fillStyle = halo
  c.beginPath()
  c.arc(x, y, r * 3.6, 0, Math.PI * 2)
  c.fill()

  c.fillStyle = withAlpha(MOON_COLOR, 0.96 * night)
  c.beginPath()
  c.arc(x, y, r, 0, Math.PI * 2)
  c.fill()
  // Three soft craters — enough for a three-year-old to see a face in it.
  c.fillStyle = withAlpha(MOON_SHADE, 0.34 * night)
  c.beginPath()
  c.arc(x - r * 0.34, y - r * 0.22, r * 0.26, 0, Math.PI * 2)
  c.arc(x + r * 0.3, y + r * 0.14, r * 0.19, 0, Math.PI * 2)
  c.arc(x - r * 0.06, y + r * 0.44, r * 0.14, 0, Math.PI * 2)
  c.fill()
}

/** Sheet resolution for the bed fade. It owns the whole glass; it can afford it. */
const OVERLAY_SIZE = 1024
/**
 * THE BED FADE TURNS AROUND THE MIDDLE OF THE SCREEN, not around the horizon.
 *
 * The backdrop's pole sits just below the bottom edge, which is what a sky over
 * a town looks like — but a field that lives ABOVE its pole, rotated most of a
 * turn, ends up entirely underneath it. That is exactly what happened: the fade
 * reported a full-opacity star field with a 250-degree rotation on it and the
 * capture had no stars in it at all, twice. A pole in the middle of the glass,
 * with the field spread evenly around it, is on screen at every angle.
 */
const OVERLAY_POLE = { x: 0.5, y: 0.5 } as const

function paint(state: PaintedState): void {
  const c = ctx2d
  if (c === null) return
  // The SAME three stops the shipped `skyTexture()` uses (0 / 0.55 / 1), so at
  // noon this canvas is the sky the game already had.
  const grd = c.createLinearGradient(0, 0, 0, SIZE)
  grd.addColorStop(0, cssHex(state.top))
  grd.addColorStop(0.55, cssHex(state.mid))
  grd.addColorStop(1, cssHex(state.horizon))
  c.fillStyle = grd
  c.fillRect(0, 0, SIZE, SIZE)

  if (state.night > 0.02) {
    paintMoon(c, state.night, state.wheel)
    const px = POLE_X * SIZE
    const py = POLE_Y * SIZE
    for (let i = 0; i < sheets.length; i++) {
      const sheet = sheets[i]
      if (sheet === undefined) continue
      const tw = 0.8 + 0.2 * Math.sin(state.twinkle * 2 + i * 2.3)
      c.save()
      c.globalAlpha = Math.min(1, state.night * tw)
      c.translate(px, py)
      c.rotate(state.wheel)
      c.translate(-px, -py)
      c.drawImage(sheet, 0, 0)
      c.restore()
    }
  }

  painted = { ...state }
  repaints++
  if (texture !== null) texture.needsUpdate = true
}

// ---------------------------------------------------------------------------
// public
// ---------------------------------------------------------------------------

/** The one backdrop texture. Created on first call, never replaced. */
export function skyBackdrop(): THREE.CanvasTexture {
  if (texture !== null) return texture
  canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const c = canvas.getContext('2d')
  if (c === null) throw new Error('time: no 2d context for the sky')
  ctx2d = c
  buildSheets()
  texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 1
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  texture.name = 'time:sky'
  return texture
}

/**
 * Repaint if — and only if — the sky has visibly moved. `wheel` is the sleep
 * fade turning the whole sky around its pole; `twinkle` is a slow clock the
 * stars breathe on, quantised by the caller so this never runs at frame rate.
 */
export function updateSky(look: DayLook, wheel = 0, twinkle = 0): void {
  skyBackdrop()
  const next: PaintedState = {
    top: look.skyTop,
    mid: look.skyMid,
    horizon: look.skyHorizon,
    night: look.night,
    wheel,
    twinkle,
  }
  const p = painted
  if (
    p !== null &&
    channelDelta(p.top, next.top) < REPAINT_DELTA &&
    channelDelta(p.mid, next.mid) < REPAINT_DELTA &&
    channelDelta(p.horizon, next.horizon) < REPAINT_DELTA &&
    Math.abs(p.night - next.night) < NIGHT_DELTA &&
    p.wheel === next.wheel &&
    p.twinkle === next.twinkle
  ) {
    return
  }
  paint(next)
}

/** Diagnostics: how many uploads this session has actually cost. */
export function skyRepaints(): number {
  return repaints
}

/**
 * THE BACKDROP'S OWN FIELD, AS AN IMAGE. Kept because it is the reference the
 * dream sheet below is defined against — same seed, same order, same first 150
 * stars — and because a tool may want to compare the two. The bed fade uses
 * `dreamSheetUrl()`.
 */
let sheetUrl: string | null = null
export function starSheetUrl(): string {
  if (sheetUrl !== null) return sheetUrl
  sheetUrl = renderSheet(0.45, STAR_COUNT)
  return sheetUrl
}

/**
 * THE SAME STARS AGAIN, DENSER, FOR THE BED FADE — the one moment the game can
 * actually show a night sky, because for three seconds it owns the whole glass
 * (the town's own tree line fills the top of the frame at every zoom the camera
 * has; see the note in `sleep.ts`). Built once and cached as a data URL, so 620
 * radial gradients are paid for exactly one time in a session, never on a frame.
 */
let dreamUrl: string | null = null
export function dreamSheetUrl(): string {
  if (dreamUrl !== null) return dreamUrl
  // 0.34 and a TIGHT halo, NOT bigger: the first attempt at fixing "sparse
  // specks" raised the radius as well as the count, and a field of mid-sized
  // soft dots reads as falling SNOW. More stars, each one small and crisp.
  dreamUrl = renderSheet(0.34, DREAM_STAR_COUNT, 2.2)
  return dreamUrl
}

function renderSheet(radiusMul: number, count: number, glowSpread = 3.4): string {
  skyBackdrop()
  const c = document.createElement('canvas')
  c.width = OVERLAY_SIZE
  c.height = OVERLAY_SIZE
  const g = c.getContext('2d')
  if (g === null) return ''
  // Not 1: see `paintField`. Four times the canvas, a bit over twice the star,
  // so a star is the same few pixels on the glass either way.
  paintField(g, OVERLAY_SIZE, radiusMul, 0, false, count, glowSpread)
  paintMoon(g, 1, 0, OVERLAY_SIZE)
  const url = c.toDataURL('image/png')
  // `paintField` leaves the moon's pole set to whatever it just drew for; put
  // the backdrop's own back, or the sky would start drawing its moon in the
  // middle of the screen from here on.
  buildSheets()
  painted = null
  return url
}

/** Where the OVERLAY sheet turns, in fractions — the CSS transform-origin. */
export const SKY_POLE = OVERLAY_POLE

/** Diagnostics: how strongly the star layer is showing in the painted sky. */
export function skyNightLevel(): number {
  return painted?.night ?? 0
}

/**
 * The far haze. The town's fog and the sky's horizon are the same colour by
 * design — that is what makes the tree line read as distance rather than as a
 * grey band — so the fog follows the horizon stop all day.
 */
export function updateFog(scene: THREE.Scene, look: DayLook): void {
  const fog = scene.fog
  if (fog instanceof THREE.Fog || fog instanceof THREE.FogExp2) {
    if (fog.color.getHex() !== look.fog) fog.color.setHex(look.fog)
  }
}
